/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnInit, signal} from '@angular/core';
import * as _ from 'lodash';
import {firstValueFrom} from 'rxjs';

import template from './creative-preview-view.component.html';
import {collateCreativePreviewLineItemData} from './preview-request-data';
import {deferSignalStateWrite} from '../../common/util/template-state';
import {CreativePreviewApiService} from '../creative-preview-api.service';

@Component({
  standalone: false,
  selector: 'adn-creative-preview-via-id',
  template
})
export class CreativePreviewViaIdComponent implements OnInit {
  @Input() creative: any;
  @Input() creativePreviewTrigger: any;
  @Input() maxWidth: string;
  @Input() maxHeight: string;

  previewTrigger: any = {};
  readonly resolved = signal(false);
  readonly previewError = signal(false);
  readonly layoutType = signal<string | undefined>(undefined);

  private lastPreviewParams: any;
  private previewVersion = 0;

  constructor(private readonly creativePreviewApi: CreativePreviewApiService) {
  }

  ngOnInit(): void {
    this.runPreview(this);

    if (this.creativePreviewTrigger) {
      this.creativePreviewTrigger.previewCreative = (previewData: any) => {
        this.runPreview(previewData);
      };
    }

    this.previewTrigger.refreshPreview = (options: any) => {
      if (!this.lastPreviewParams) {
        return;
      }
      this.runPreview(_.assign({}, this.lastPreviewParams, options));
    };
  }

  private runPreview(params: any): void {
    const previewVersion = ++this.previewVersion;
    const creative = params.creative || this.creative;
    this.lastPreviewParams = {creative};
    this.resolved.set(false);
    this.previewError.set(false);
    if (!creative || !creative.id ||
      _.find(creative.validationWarnings, ['preventsAdServing', true]) ||
      !creative.layout) {
      this.resolved.set(true);
      return;
    }

    this.layoutType.set(_.get(creative, ['layout', 'detectedCategory']));
    const creativeWidth = this.getPreviewDimension(params.creativeWidth, creative.width, creative.height);
    const creativeHeight = this.getPreviewDimension(params.creativeHeight, creative.height, creative.width);
    const apiPreviewData: any = {
      creativeId: creative.id,
      creativeWidth,
      creativeHeight
    };
    _.assign(apiPreviewData, collateCreativePreviewLineItemData(creative));

    firstValueFrom(this.creativePreviewApi.get(creative.id)).then((data) => {
      if (previewVersion !== this.previewVersion) {
        return;
      }
      const html = data.html;
      const previewPayload = {
        pageTitle: 'Full-Screen Creative View',
        creativeContent: html,
        creativeWidth,
        creativeHeight,
        dimensionType: creative.dimensionType,
        maxWidth: this.maxWidth,
        maxHeight: this.maxHeight,
        center: false
      };

      deferSignalStateWrite(() => {
        if (previewVersion !== this.previewVersion) {
          return;
        }
        this.previewTrigger.previewInline?.(previewPayload, apiPreviewData);
        this.resolved.set(true);
      });
    }).catch(() => {
      if (previewVersion !== this.previewVersion) {
        return;
      }
      deferSignalStateWrite(() => {
        if (previewVersion !== this.previewVersion) {
          return;
        }
        this.previewError.set(true);
        this.resolved.set(true);
      });
    });
  }

  private getPreviewDimension(overrideValue: any, primaryValue: any, fallbackValue: any): number {
    return Number(overrideValue) || Number(primaryValue) || Number(fallbackValue) || 300;
  }
}
